import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  GallerySection,
  SectionTitle,
  GalleryGrid,
  GalleryItem,
  ImageContainer,
  LightboxOverlay,
  LightboxContent,
  LightboxImage,
  LightboxCaption,
  LightboxNav,
  NavButton,
  CloseButton,
  ImageCounter,
  ShowMoreButton,
  ShowMoreButtonContainer,
} from "./Gallery.styled";

// Import from the separate file
import galleryImages from "./galleryImages";

const Gallery = ({ id = "gallery", background, images }) => {
  const { t } = useTranslation();
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Use the imported galleryImages as default
  const imagesToUse = images || galleryImages;

  // Show only 6 images in grid by default
  const displayedImages = imagesToUse.slice(0, 6);

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setIsLightboxOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = useCallback(() => {
    setIsLightboxOpen(false);
    document.body.style.overflow = "unset";
  }, []);

  const goToPrevious = useCallback(() => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? imagesToUse.length - 1 : prevIndex - 1
    );
  }, [imagesToUse.length]);

  const goToNext = useCallback(() => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === imagesToUse.length - 1 ? 0 : prevIndex + 1
    );
  }, [imagesToUse.length]);

  const handleShowMore = () => {
    // Open lightbox at first image when "Show More" is clicked
    openLightbox(0);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isLightboxOpen) return;

      switch (event.key) {
        case "Escape":
          closeLightbox();
          break;
        case "ArrowLeft":
          goToPrevious();
          break;
        case "ArrowRight":
          goToNext();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isLightboxOpen, closeLightbox, goToPrevious, goToNext]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  const currentImage = imagesToUse[currentImageIndex];

  return (
    <>
      <GallerySection id={id} background={background}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <SectionTitle>{t("gallery.title")}</SectionTitle>

              <GalleryGrid>
                {displayedImages.map((image, index) => (
                  <GalleryItem
                    key={image.id}
                    onClick={() => openLightbox(index)}
                  >
                    <ImageContainer
                      style={{
                        backgroundImage: `url(${image.src})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}
                    ></ImageContainer>
                  </GalleryItem>
                ))}
              </GalleryGrid>

              {/* Show More button - only show if there are more than 6 images */}
              {imagesToUse.length > 6 && (
                <ShowMoreButtonContainer>
                  <ShowMoreButton onClick={handleShowMore}>
                    {t("gallery.showMore") || "Show More"}
                  </ShowMoreButton>
                </ShowMoreButtonContainer>
              )}
            </div>
          </div>
        </div>
      </GallerySection>

      {/* Lightbox/Modal */}
      <LightboxOverlay isOpen={isLightboxOpen} onClick={closeLightbox}>
        <CloseButton onClick={closeLightbox}>×</CloseButton>
        <LightboxContent onClick={(e) => e.stopPropagation()}>
          <LightboxImage
            style={{
              backgroundImage: `url(${currentImage?.src})`,
              backgroundSize: "contain",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
            }}
          />

          <LightboxCaption>{currentImage?.description}</LightboxCaption>

          <LightboxNav>
            <NavButton onClick={goToPrevious}>←</NavButton>

            <ImageCounter>
              {currentImageIndex + 1} / {imagesToUse.length}
            </ImageCounter>

            <NavButton onClick={goToNext}>→</NavButton>
          </LightboxNav>
        </LightboxContent>
      </LightboxOverlay>
    </>
  );
};

export default Gallery;
